// Maps the public marketplace DTOs into the shapes the landing cards render.
//
//   GET /api/public/catalog/books       -> { items, total, page, limit, totalPages }
//   GET /api/public/catalog/journals    -> { items, total, page, limit, totalPages }
//   GET /api/public/catalog/resources   -> { items, total, page, limit, totalPages }
//
// Resource items use `itemType` (PROJECT_TOPIC, SEMINAR_TOPIC, …) and a
// route-friendly `type` (project-topics, seminar-topics, …). Books and journals
// are also returned by the mixed resources feed, so the same mapper covers both.

import {imageUrl, shortDate} from "../../lib/format";

export const MATERIAL_TYPE_BY_ITEM = {
  PROJECT_TOPIC: "project-topics",
  SEMINAR_TOPIC: "seminar-topics",
  RESEARCH_MATERIAL: "research-materials",
  MANUAL: "manuals",
  BOOK: "books",
  JOURNAL: "journals",
};

export const MATERIAL_TYPE_LABELS = {
  "project-topics": "Project topic",
  "seminar-topics": "Seminar topic",
  "research-materials": "Research material",
  manuals: "Manual",
  books: "Book",
  journals: "Journal",
};

export const MATERIAL_TYPES = [
  "books",
  "journals",
  "seminar-topics",
  "project-topics",
  "research-materials",
  "manuals",
];

const numberOrZero = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const placeOf = (resource) =>
  resource?.resourcePerson?.name ||
  (Array.isArray(resource?.departments) ? resource.departments[0]?.name : "") ||
  resource?.fieldOfStudy ||
  "";

const typeOf = (resource) =>
  resource?.type || MATERIAL_TYPE_BY_ITEM[String(resource?.itemType || "").toUpperCase()] || "";

/** Shared card contract for every marketplace item. */
function baseCard(api, {kind, type}) {
  const price = numberOrZero(api.price);
  return {
    kind,
    type,
    itemType: api.itemType || "",
    id: api.id,
    slug: api.slug || "",
    title: api.title || "Untitled material",
    subtitle: placeOf(api),
    badge: MATERIAL_TYPE_LABELS[type] || "",
    image: imageUrl(api.thumbnailUrl || api.coverImage),
    price,
    isFree: api.isFree !== undefined ? Boolean(api.isFree) : price === 0,
    downloads: numberOrZero(api.downloads),
    level: api.academicLevel || "",
    fieldOfStudy: api.fieldOfStudy || "",
    fileFormat: api.fileFormat || "",
    publisher: api.publisher || null,
    resourcePerson: api.resourcePerson || null,
    departments: Array.isArray(api.departments) ? api.departments : [],
    publishedAt: api.publishedAt || api.uploadedAt || "",
    uploadedAt: api.uploadedAt || api.publishedAt || "",
  };
}

/** GET /api/public/catalog/books and GET /api/learners/books items. */
export function mapPublicBook(api = {}) {
  const card = baseCard(api, {kind: "book", type: "books"});
  return {
    ...card,
    author: api.author || "",
    subtitle: api.author || api.publisher?.name || "",
    badge: "Book",
    meta: [api.publisher?.name, shortDate(api.uploadedAt || api.updatedAt)]
      .filter(Boolean)
      .join(" · "),
  };
}

/** GET /api/public/catalog/journals and GET /api/learners/journals items. */
export function mapPublicJournal(api = {}) {
  const card = baseCard(api, {kind: "journal", type: "journals"});
  return {
    ...card,
    author: api.author || "",
    subtitle: api.author || api.publisher?.name || "",
    badge: "Journal",
    meta: [api.publisher?.name, shortDate(api.uploadedAt || api.updatedAt)]
      .filter(Boolean)
      .join(" · "),
  };
}

/** GET /api/public/catalog/resources items (all marketplace types). */
export function mapPublicResource(api = {}) {
  const type = typeOf(api);
  const card = baseCard(api, {
    kind: type === "books" ? "book" : type === "journals" ? "journal" : "resource",
    type,
  });

  return {
    ...card,
    summary: api.summary || api.description || "",
    meta: [
      api.academicLevel,
      api.fieldOfStudy,
      api.fileFormat,
      shortDate(api.publishedAt),
    ]
      .filter(Boolean)
      .join(" · "),
    inCart: Boolean(api.inCart),
    viewer: api.viewer || null,
  };
}

/** In-app detail route for a marketplace material. */
export function materialDetailPath(material) {
  const type =
    material?.type ||
    MATERIAL_TYPE_BY_ITEM[String(material?.itemType || "").toUpperCase()] ||
    "";
  const idOrSlug =
    type === "books" || type === "journals"
      ? material?.id
      : material?.slug || material?.id;
  if (!type || !idOrSlug) return "";
  return `/materials/${encodeURIComponent(type)}/${encodeURIComponent(idOrSlug)}`;
}

/** Human label for a `type` query param. */
export function materialTypeLabel(type) {
  return MATERIAL_TYPE_LABELS[type] || "Material";
}
